import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Shield, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../lib/api";

const BoardPage = () => {
  const { t, lang } = useLanguage();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/board-members")
      .then(({ data }) => setMembers(Array.isArray(data) ? data : []))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="board-page">
      <Seo
        title={t.seo.board.title}
        description={t.seo.board.description}
        path={lang === "en" ? "/board" : "/conseil-administration"}
        lang={lang}
      />
      <Navbar />

      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-b from-[#002B49] to-[#001F35] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #C5A059 0%, transparent 50%)" }} />
        <div className="container-narrow relative">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-[#E8C77E] mb-6 text-sm transition-colors" data-testid="back-home">
            <ArrowLeft size={14} /> {t.boardPage.back}
          </Link>
          <span className="eyebrow text-[#E8C77E]" data-testid="board-eyebrow">{t.boardPage.eyebrow}</span>
          <span className="block w-16 h-px bg-[#C5A059] mt-4 mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.1]" data-testid="board-title">
            {t.boardPage.title}
          </h1>
          <p className="text-white/85 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed" data-testid="board-intro">
            {t.boardPage.intro}
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Badge icon={Shield} label={t.boardPage.badges.audited} />
            <Badge icon={Heart} label={t.boardPage.badges.committed} />
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container-narrow">
          {loading ? (
            <div className="text-center text-[#64748B] py-12">{t.boardPage.loading}</div>
          ) : members.length === 0 ? (
            <div className="text-center text-[#64748B] py-12">{t.boardPage.empty}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              {members.map((m, i) => (
                <motion.article
                  key={m.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="bg-white rounded-2xl overflow-hidden border border-black/5 shadow-[0_8px_40px_-20px_rgba(0,43,73,0.15)] flex flex-col md:flex-row"
                  data-testid={`board-member-${i}`}
                >
                  {m.image_url && (
                    <div className="md:w-44 md:shrink-0">
                      <img
                        src={m.image_url}
                        alt={m.name}
                        className="w-full h-48 md:h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-6 md:p-7 flex-1">
                    <h3 className="font-serif text-xl md:text-2xl text-[#002B49] font-semibold leading-tight">
                      {m.name}
                    </h3>
                    <div className="text-[#C5A059] text-sm font-medium tracking-wide mt-1">
                      {lang === "en" ? (m.role_en || m.role_fr) : (m.role_fr || m.role_en)}
                    </div>
                    <p className="text-[#334155] text-sm leading-relaxed mt-4">
                      {lang === "en" ? (m.bio_en || m.bio_fr) : (m.bio_fr || m.bio_en)}
                    </p>
                  </div>
                </motion.article>
              ))}
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-20 text-center bg-white rounded-2xl p-10 md:p-14 border border-black/5"
          >
            <h2 className="font-serif text-2xl md:text-3xl text-[#002B49]" data-testid="board-cta-title">
              {t.boardPage.ctaTitle}
            </h2>
            <p className="text-[#64748B] mt-3 max-w-xl mx-auto">{t.boardPage.ctaSubtitle}</p>
            <Link
              to="/#donate"
              className="btn-primary inline-flex items-center mt-6"
              data-testid="board-cta-donate"
            >
              {t.boardPage.ctaButton}
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const Badge = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 text-xs text-white/80 backdrop-blur-sm">
    <Icon size={12} className="text-[#E8C77E]" /> {label}
  </span>
);

export default BoardPage;
